/* @flow strict */

import * as React from 'react';
import { TicketFeeType } from '/client/src/constants/';
import { currency } from '/client/src/constants';
import { format } from '/client/src/utils/amountTransformation';
import Terms from './termsModal';
import './purchase.scss';
import _ from 'lodash';
import { DefaultContainerContext } from '/client/src/contexts/defaultContainer';


type PurchasePropsType = {
  eventDetail: {
    default_currency: string,
    terms_and_conditions: string
  },
  cartItems: [],
  handleSetTotal: () => void
};

const PurchaseTable = (props: PurchasePropsType): React.Element<'div'> => {
  const context = React.useContext(DefaultContainerContext);

  const { cartItems, eventDetail, handleSetTotal } = props;
  const [isTermsOpen, setTermsOpen] = React.useState(false);

  const getCartItemPrice = (cartItem: {}): number => {
    const price = _.get(cartItem, 'price', 0) || _.get(cartItem, 'prefilled_amount', 0);

    return price * cartItem.countSelectedCartItems;
  };

  const getTotalFee = (): number => {
    return _.reduce(cartItems, (sum: number, cartItem: {}): number => {
      let fee = cartItem.merchant_fee || 0;

      if (cartItem.fee_type === TicketFeeType.ExcludeFee) {
        fee = (
          cartItem.merchant_fee +
          cartItem.payment_method_additional_fee +
          cartItem.payment_method_additional_fix_fee +
          cartItem.sparxo_fee
        );
      }
      return sum + fee;
    }, 0);
  };

  const getSubtotal = (): number => {
    return _.reduce(cartItems, (sum: number, cartItem: {}): number => sum + getCartItemPrice(cartItem), 0);
  };

  const getTotalTax = (): number => {
    return _.reduce(cartItems, (sum: number, cartItem: {}): number => {
      let tax = cartItem.tax || 0;
      const _price = getCartItemPrice(cartItem);

      if (cartItem.tax_use_percentage) {
        tax = _price * (cartItem.tax_percentage / 100);
      }
      return sum + tax;
    }, 0);
  };

  const getTotalDiscount = (): number => {
    return _.reduce(cartItems, (sum: number, cartItem: {}): number => sum + cartItem.discount_amount || 0, 0);
  };

  const subTotal = getSubtotal();
  const totalTax = getTotalTax();
  const totalFee = getTotalFee();
  const totalDiscount = getTotalDiscount();
  const totalPrice = Math.max((subTotal + totalTax + totalFee - totalDiscount), 0);

  React.useEffect(() => {
    handleSetTotal(totalPrice);
  }, []);

  return (
    <>
      <div>
        <div className="sparxo-checkout-purchase_purchaseTitle">Confirm purchase</div>
        <div className="sparxo-checkout-purchase_table">
          {
            !!_.size(cartItems) && cartItems.map((cartItem: {}): React.Element<'div'> => (
              <div
                key={cartItem.id}
                className="sparxo-checkout-purchase_table__row"
              >
                <div className="sparxo-checkout-purchase_table__nameWrapper">
                  <div>{`${cartItem.countSelectedCartItems}x`}</div>
                  <div>{cartItem.name}</div>
                </div>
                <div>
                  {format(currency[`${context.profileCurrent.currency}`], getCartItemPrice(cartItem), false, true)}
                </div>

              </div>
            ))
          }
          <hr style={{ borderTop: '1px solid #AAAAAA' }}/>
          <div className="sparxo-checkout-purchase_table__row">
            <div>Subtotal</div>
            <div>{format(currency[`${context.profileCurrent.currency}`], subTotal, false, true)}</div>
          </div>
          <div className="sparxo-checkout-purchase_table__row">
            <div>Tax</div>
            <div>{format(currency[`${context.profileCurrent.currency}`], totalTax, false, true)}</div>
          </div>
          <div className="sparxo-checkout-purchase_table__row">
            <div>Service Fee</div>
            <div>{format(currency[`${context.profileCurrent.currency}`], totalFee, false, true)}</div>
          </div>
          {(totalDiscount > 0) &&
          <div className="sparxo-checkout-purchase_table__row_bold">
            <div>Discount</div>
            <div className="sparxo-checkout-purchase_discount">
              {format(currency[`${context.profileCurrent.currency}`], totalDiscount, false, true)}
            </div>
          </div>
          }
          <div className="sparxo-checkout-purchase_table__row_bold">
            <div>Total</div>
            <div>{format(currency[`${context.profileCurrent.currency}`], totalPrice, false, true)}</div>
          </div>
        </div>
        {
          eventDetail.terms_and_conditions &&
          <div className="sparxo-checkout-purchase_terms">By clicking “Check out” you agree to these
            <span onClick={(): void => setTermsOpen(true)}>terms and conditions</span>
          </div>
        }
      </div>

      <Terms
        isOpen={isTermsOpen}
        handleClose={(): void => setTermsOpen(false)}
        content={eventDetail.terms_and_conditions}
      />

    </>
  );
};

export default PurchaseTable;
